import { createMemo, createUniqueId, type JSX } from "solid-js";

interface Props {
    /** Valor armazenado em E.164 completo (ex: "+5511999998888"). */
    value: string;
    /** Recebe E.164 atualizado a cada teclada. String vazia se input vazio. */
    onInput: (e164: string) => void;
    label?: string;
    /** Mensagem de erro abaixo do input (opcional). */
    error?: string;
    fullWidth?: boolean;
    required?: boolean;
}

/**
 * Input de WhatsApp travado em +55 (Brasil). Mascara automaticamente
 * (DDD) 9 NNNN-NNNN. Estado interno guarda apenas digitos; emite E.164
 * completo onInput.
 *
 * Quando o produto for internacional, trocar o prefixo fixo por um
 * Select de paises mantendo a mesma interface (E.164 dentro).
 *
 * A11y:
 * - label associada via for/id
 * - aria-describedby aponta pra erro quando presente
 * - inputmode=tel + autocomplete=tel-national pra teclado numerico mobile
 * - prefixo fica fora do input (aria-hidden) e ainda visualmente colado
 */
export function WhatsappInput(props: Props): JSX.Element {
    const id = createUniqueId();
    const errId = `${id}-err`;

    // Digitos brutos do BR (sem o +55). Maximo 11 (DDD2 + 9 + 8).
    const localDigits = createMemo(() => {
        const raw = props.value ?? "";
        const onlyDigits = raw.replace(/\D/g, "");
        // Se vier com 55 na frente, remove.
        if (onlyDigits.startsWith("55") && onlyDigits.length > 11) {
            return onlyDigits.slice(2, 13);
        }
        return onlyDigits.slice(-11);
    });

    const formatted = createMemo(() => formatBR(localDigits()));

    function handleInput(e: InputEvent) {
        const target = e.currentTarget as HTMLInputElement;
        const digits = target.value.replace(/\D/g, "").slice(0, 11);
        const e164 = digits.length === 0 ? "" : `+55${digits}`;
        props.onInput(e164);
        // Reflete o valor formatado no input (Solid controla via prop, mas
        // como passamos `value`, garantimos sync apos mascara).
        target.value = formatBR(digits);
    }

    return (
        <div class={`ui-field ${props.fullWidth ? "ui-field-full" : ""}`}>
            <label class="ui-label" for={id}>
                {props.label ?? "WhatsApp"}
                {props.required ? <span class="ui-label-req" aria-hidden="true"> *</span> : null}
            </label>
            <div class={`ui-phone ${props.error ? "is-invalid" : ""}`}>
                <span class="ui-phone-prefix" aria-hidden="true">
                    <span class="ui-phone-flag">BR</span>
                    <span class="ui-phone-cc">+55</span>
                </span>
                <input
                    id={id}
                    type="tel"
                    inputmode="tel"
                    autocomplete="tel-national"
                    class="ui-phone-input"
                    value={formatted()}
                    placeholder="(11) 9 8888-7777"
                    onInput={handleInput}
                    aria-describedby={props.error ? errId : undefined}
                    aria-invalid={!!props.error}
                />
            </div>
            {props.error ? (
                <p class="ui-field-err" id={errId} role="alert">
                    {props.error}
                </p>
            ) : null}
        </div>
    );
}

/**
 * Formata digitos BR no padrao (XX) 9 XXXX-XXXX.
 *
 * - 0  digitos: ""
 * - 1-2: "(XX"
 * - 3:   "(XX) X"
 * - 4-7: "(XX) X XXXX"
 * - 8-11: "(XX) X XXXX-XXXX"
 */
function formatBR(digits: string): string {
    if (!digits) return "";
    const d = digits.slice(0, 11);
    const ddd = d.slice(0, 2);
    const rest = d.slice(2);
    if (rest.length === 0) return `(${ddd}`;
    if (rest.length <= 1) return `(${ddd}) ${rest}`;
    if (rest.length <= 5) return `(${ddd}) ${rest[0]} ${rest.slice(1)}`;
    return `(${ddd}) ${rest[0]} ${rest.slice(1, 5)}-${rest.slice(5)}`;
}

/** Valida E.164 brasileiro: +55 + 10 ou 11 digitos. */
export function isValidBrazilWhatsapp(e164: string): boolean {
    return /^\+55\d{10,11}$/.test(e164);
}
